import React from 'react';

// Two-step finalisation for adding a race. After the user fills in the
// race form and clicks "Add", this modal asks them to declare what KIND
// of race it is — goal race (drives taper, cuts volume the week before)
// vs tune-up race (stays as a quality session inside the regular weekly
// volume).
//
// Safe default: dismissing the modal (X / outside-click / Escape) commits
// the race as a TUNE-UP (is_goal_race=false). That preserves the user's
// weekly volume — the conservative choice. The user can always edit the
// race later and change its type.
//
// Props:
//   raceName  — display only (e.g. "10K הרצליה")
//   raceDate  — display only (e.g. "12/06/2026")
//   onSelect(isGoalRace: boolean)  — called when the user picks an option
//                                    or dismisses (with false)
export default function RaceTypePickerModal({ raceName, raceDate, onSelect }) {
  const dismissAsTuneup = () => onSelect(false);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4"
      dir="rtl"
      onClick={dismissAsTuneup}
    >
      <div
        className="w-full max-w-md rounded-[16px] bg-surface border border-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-text text-lg font-bold">איזה סוג מרוץ זה?</h2>
          {(raceName || raceDate) && (
            <p className="text-text-body text-sm mt-1">
              {raceName}{raceName && raceDate ? ' · ' : ''}{raceDate}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="px-5 py-4 text-sm text-text-body">
          הבחירה הזו קובעת איך התכנית השבועית תיבנה לקראת המרוץ:
        </div>

        {/* Options — Tune-up first because it's the safe default (preserves
            weekly volume; runners overwhelmingly add tune-up races, not
            goal races). Goal Race is the explicit opt-in for taper. */}
        <div className="px-5 pb-4 space-y-3">
          {/* Tune-up (default visual) */}
          <button
            type="button"
            onClick={() => onSelect(false)}
            className="w-full text-right rounded-[12px] bg-transparent border-[1.5px] border-accent transition p-4"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-text font-bold">
                  אימון / חימום (Tune-up)
                  <span className="text-accent text-[12px] mr-2 font-semibold">ברירת מחדל</span>
                </div>
                <div className="text-text-body text-xs mt-1 leading-relaxed">
                  מרוץ אימון או בדיקת כושר. <span className="font-bold">ללא טייפר</span> — הנפח השבועי נשמר במלואו, המרוץ נכלל כ-Quality Session ביום שלו.
                </div>
              </div>
            </div>
          </button>

          {/* Goal Race (explicit opt-in) */}
          <button
            type="button"
            onClick={() => onSelect(true)}
            className="w-full text-right rounded-[12px] bg-transparent border border-border transition p-4"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-text font-bold">מרוץ מטרה (Goal Race)</div>
                <div className="text-text-body text-xs mt-1 leading-relaxed">
                  המרוץ העיקרי שהתאמנת אליו. התכנית תכלול <span className="font-bold">טייפר</span> — הפחתת נפח מדורגת בשבועות שלפניו לרעננות מקסימלית ביום המרוץ.
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Footer hint */}
        <div className="px-5 pb-4">
          <button
            type="button"
            onClick={dismissAsTuneup}
            className="w-full min-h-[44px] text-[13px] text-text-muted underline underline-offset-2"
          >
            ביטול (יישמר כאימון לבטיחות הנפח)
          </button>
        </div>
      </div>
    </div>
  );
}
